import { useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { usePermission } from 'hooks';
import RpcTypes from 'constants/rpcTypes';
import permissionReasons from 'constants/permissionReasons';

interface Tab {
  id: string;
  path: string;
  permission: RpcTypes;
}

interface UseTabbedFormProps {
  initialTabs: Tab[];
  basePath: string;
}

export const useTabbedForm = ({
  initialTabs,
  basePath,
}: UseTabbedFormProps) => {
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const { isEnabled, isDisabledByReason } = usePermission();
  const [selectedTabId, setSelectedTabId] = useState('');

  const permittedTabs = useMemo(() => {
    return initialTabs.filter(
      (tab) =>
        isEnabled(tab.permission) ||
        isDisabledByReason(
          tab.permission,
          permissionReasons.REASON_IS_NOT_AVAILABLE_FOR_SUPPORT
        )
    );
  }, [initialTabs]);

  const tabs = useMemo(() => {
    return permittedTabs.map((tab) => ({
      ...tab,
      isCurrent: selectedTabId === tab.id,
    }));
  }, [selectedTabId]);

  useEffect(() => {
    const availableTab = permittedTabs.find(({ path }) => path === params.id);

    if (!availableTab) {
      const { id, path } = permittedTabs[0];
      setSelectedTabId(id);
      history.replace(`${basePath}/${path}`);
      return;
    }

    setSelectedTabId(availableTab.id);
  }, [params.id]);

  const handleChangeTab = (tabId: string) => {
    const { path } = tabs.find(({ id }) => id === tabId)!;
    history.push(`${basePath}/${path}`);
  };

  return {
    selectedTabId,
    tabs,
    handleChangeTab,
  };
};
